import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const TabsContext = createContext(null);
const KEY = 'bbc_staff_tabs_v1';
const MAX_TABS = 8;

// Provided by StaffTabKeepAliveShell around EACH tab's own route tree
// (value = true only for the currently active/visible tab, false for every
// backgrounded one). Consumed by ProtectedRoute/StaffRoute/AdminRoute so a
// denied guard in a hidden tab renders nothing instead of <Navigate> —
// otherwise its redirect effect re-fires on every subsequent navigation
// (react-router's `navigate` identity changes per location) and hijacks
// the URL away from whatever tab the user actually switched to. Defaults
// to true so pages rendered OUTSIDE the shell (customer/investor portal,
// or any staff page before this context is set up) behave exactly as before.
export const TabActiveContext = createContext(true);
// Same flag as a stable ref ({ current }) — read inside callbacks without subscribing to re-renders.
export const TabActiveRefContext = createContext({ current: true });

// Module-level flag (not React state — cheap, synchronous, read-once-per-nav)
// so the "+" (new tab) button can mark "this upcoming navigate() call should
// open a brand new tab" right before calling navigate(). Every OTHER
// navigation (sidebar clicks, swipe, back, links) defaults to updating the
// tab that's currently active in place — like a real browser tab — so this
// flag only needs to exist for the explicit "open a new tab" action.
let _openNewTabPending = false;
export function markOpenNewTab() { _openNewTabPending = true; }
// Always clears the flag when called — callers must call this exactly once
// per navigation (even if they don't need the value) so a stale flag never
// leaks into an unrelated later navigation.
export function consumeOpenNewTab() {
  const v = _openNewTabPending;
  _openNewTabPending = false;
  return v;
}

// Same pattern as above, but for "switching to an already-open tab" (tab
// strip click, minimised chip restore, Ctrl/Cmd+K tab jump, close-and-fall-
// back-to-another-tab). ScrollRestoration reads this to know it must NOT
// force scroll-to-top for this navigation — StaffTabKeepAliveShell owns
// restoring that tab's own remembered scroll position instead.
const _scrollByPath = new Map();
export function getScrollFor(path) {
  return _scrollByPath.get(path) || 0;
}
// Save-only (no tab-switch flag) — used when the tab being left stays open
// in the background but the navigation itself is legitimately "fresh" and
// SHOULD scroll-to-top (e.g. opening a brand new "+" tab). We still want to
// remember where that backgrounded tab was, for whenever it's switched back to.
export function saveScrollFor(path) {
  if (!path) return;
  _scrollByPath.set(path, window.scrollY || document.documentElement.scrollTop || 0);
}

let _tabSwitchPending = false;
// Two independent one-shot flags set by the SAME markTabSwitchNav() call —
// ScrollRestoration.js and StaffTabKeepAliveShell.jsx each consume their
// own copy so neither one silently swallows the signal the other needs
// (a single shared consume-and-clear flag caused whichever effect ran
// first to blind the other, which is what broke the sessionStorage-based
// listing→detail→POP scroll restore across Customers/Loans Hub/etc).
let _tabSwitchPendingForShell = false;
// Remember the query string / hash a tab was left on (e.g. Customers ?tab=potential) so
// switching back re-opens the exact same view instead of the bare path.
const _searchByPath = new Map();
export function getSearchFor(path) {
  return _searchByPath.get(path) || '';
}
export function markTabSwitchNav(fromPath) {
  _tabSwitchPending = true;
  _tabSwitchPendingForShell = true;
  const leaving = fromPath || window.location.pathname;
  if (leaving === window.location.pathname) _searchByPath.set(leaving, window.location.search + window.location.hash);
  saveScrollFor(leaving);
}
export function consumeTabSwitchNav() {
  const v = _tabSwitchPending;
  _tabSwitchPending = false;
  return v;
}
export function consumeTabSwitchNavForShell() {
  const v = _tabSwitchPendingForShell;
  _tabSwitchPendingForShell = false;
  return v;
}

// Called on logout — tabs (including any admin-only ones) must NOT leak
// into the next account that logs in on the same browser/device.
export function clearStoredTabs() {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}

const load = () => {
  try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; }
};

export function TabsProvider({ children }) {
  const saved = load();
  const [tabs, setTabs] = useState(Array.isArray(saved.tabs) ? saved.tabs : []);
  const [minimized, setMinimized] = useState(Array.isArray(saved.minimized) ? saved.minimized : []);
  const [pinned, setPinned] = useState(Array.isArray(saved.pinned) ? saved.pinned : []);
  const [maximized, setMaximized] = useState(false);
  const pinnedRef = useRef(pinned);
  useEffect(() => { pinnedRef.current = pinned; }, [pinned]);

  // Tracks "which path was active right before this one" ACROSS navigations.
  // Must live here (TabsProvider is mounted once, high up the tree) rather
  // than as a ref inside HeaderTabsBar — HeaderTabsBar is re-mounted on
  // every full page/route change (each staff page wraps itself in its own
  // <StaffLayout>), so a ref local to it would reset on every navigation
  // and always look like "no previous path", breaking the in-place rebind.
  const lastPathRef = useRef(null);
  const consumeLastPath = useCallback((current) => {
    const prev = lastPathRef.current;
    lastPathRef.current = current;
    return prev;
  }, []);

  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify({ tabs, minimized, pinned })); } catch { /* ignore */ }
  }, [tabs, minimized, pinned]);

  const addTab = useCallback((path, title) => {
    if (!path) return;
    setTabs((prev) => {
      if (prev.some((t) => t.path === path)) {
        return prev.map((t) => (t.path === path ? { ...t, title: title || t.title } : t));
      }
      let next = [...prev, { path, title: title || path }];
      if (next.length > MAX_TABS) {
        // drop the oldest tab that isn't the one just opened and isn't pinned
        const idx = next.findIndex((t) => t.path !== path && !pinnedRef.current.includes(t.path));
        if (idx >= 0) {
          next.splice(idx, 1);
        } else {
          // Every other tab is pinned — refuse to grow past MAX_TABS rather
          // than accumulating unbounded tabs (or evicting a pinned one).
          return prev;
        }
      }
      return next;
    });
    setMinimized((m) => m.filter((p) => p !== path)); // opening restores
  }, []);

  const closeTab = useCallback((path) => {
    setTabs((prev) => prev.filter((t) => t.path !== path));
    setMinimized((m) => m.filter((p) => p !== path));
    setPinned((p) => p.filter((x) => x !== path));
  }, []);

  const closeAllTabs = useCallback(() => {
    setTabs((prev) => prev.filter((t) => pinnedRef.current.includes(t.path)));
    setMinimized((m) => m.filter((p) => pinnedRef.current.includes(p)));
  }, []);

  const minimizeTab = useCallback((path) => {
    setMinimized((m) => (m.includes(path) ? m : [...m, path]));
    setMaximized(false);
  }, []);

  const restoreTab = useCallback((path) => {
    setMinimized((m) => m.filter((p) => p !== path));
  }, []);

  const togglePinTab = useCallback((path) => {
    setPinned((p) => (p.includes(path) ? p.filter((x) => x !== path) : [...p, path]));
  }, []);

  const reorderTabs = useCallback((fromPath, toPath) => {
    if (fromPath === toPath) return;
    setTabs((prev) => {
      const fromIdx = prev.findIndex((t) => t.path === fromPath);
      const toIdx = prev.findIndex((t) => t.path === toPath);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  }, []);

  // Rebind an existing tab to a new path/title in place (same position,
  // pin/minimise state) instead of opening a sibling tab — used for
  // in-page record browsing: swipe left/right, prev/next sibling links,
  // and any navigate(path, { replace: true }) call.
  const replaceTab = useCallback((oldPath, newPath, title) => {
    if (!oldPath || !newPath || oldPath === newPath) return;
    setTabs((prev) => prev.map((t) => (t.path === oldPath ? { path: newPath, title: title || newPath } : t)));
    setMinimized((m) => m.map((p) => (p === oldPath ? newPath : p)));
    setPinned((p) => p.map((x) => (x === oldPath ? newPath : x)));
  }, []);

  const toggleMaximize = useCallback(() => setMaximized((v) => !v), []);

  return (
    <TabsContext.Provider value={{
      tabs, minimized, pinned, maximized,
      addTab, closeTab, closeAllTabs, minimizeTab, restoreTab, togglePinTab, reorderTabs, replaceTab, toggleMaximize, setMaximized,
      consumeLastPath,
    }}>
      {children}
    </TabsContext.Provider>
  );
}

export const useTabs = () => useContext(TabsContext) || {
  tabs: [], minimized: [], pinned: [], maximized: false,
  addTab: () => {}, closeTab: () => {}, closeAllTabs: () => {}, minimizeTab: () => {}, restoreTab: () => {},
  togglePinTab: () => {}, reorderTabs: () => {}, replaceTab: () => {}, toggleMaximize: () => {}, setMaximized: () => {},
  consumeLastPath: () => null,
};
